import { useState } from 'react';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Skeleton,
  Spinner,
} from '@monksflow/monks-ui';
import clsx from 'clsx';
import {
  CheckCheckIcon,
  CheckIcon,
  ListOrderedIcon,
  LockIcon,
  RotateCcwIcon,
  ShuffleIcon,
  CircleDotIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  useAssetApprovalChain,
  type AssetApprovalChain,
  type AssetApprovalChainStep,
} from '@/entities/asset-approval-chain';
import { useCurrentUserId } from '@/entities/user';
import { notificationService } from '@/lib/services/notification.service';
import { formatShortDate } from '@/shared/utils/format';
import { getInitials } from '@/shared/utils/getInitials';
import { TruncatedText } from '@/shared/ui/TruncatedText';
import { parseProblemDetail } from '@/shared/utils/problem-details';

import { useApproveAssetStep } from '../../api/useApproveAssetStep.mutation';
import { useOverrideAssetStep } from '../../api/useOverrideAssetStep.mutation';
import { useUnapproveAssetStep } from '../../api/useUnapproveAssetStep.mutation';
import { useUndoAssetStepOverride } from '../../api/useUndoAssetStepOverride.mutation';
import { useApprovalOverride } from '../../model/useApprovalOverride';
import { RoleApproverRow } from './RoleApproverRow';
import {
  executionModeLabel,
  orderedSteps,
  requiredApprovalCount,
  stepOverride,
  stepReviewers,
  stepStatusLabel,
  stepsApprovedLabel,
  type StepReviewer,
} from './timeline.utils';

export interface ApprovalTimelineProps {
  projectId: string;
  assetId: string;
  /** Folder in view — threaded to the mutations so the files list refreshes. */
  folderId?: string;
}

export function ApprovalTimeline({ projectId, assetId, folderId }: ApprovalTimelineProps) {
  const { t } = useTranslation('projects');

  const { data: approvalChain, isLoading } = useAssetApprovalChain(projectId, assetId);
  const currentUserId = useCurrentUserId();
  const canOverride = useApprovalOverride();

  const approveStep = useApproveAssetStep({ projectId, folderId });
  const unapproveStep = useUnapproveAssetStep({ projectId, folderId });
  const overrideStep = useOverrideAssetStep({ projectId, folderId });
  const undoOverride = useUndoAssetStepOverride({ projectId, folderId });

  const [unapproveTargetStepId, setUnapproveTargetStepId] = useState<string | null>(null);
  const [overrideTargetStepId, setOverrideTargetStepId] = useState<string | null>(null);
  const [undoOverrideTargetStepId, setUndoOverrideTargetStepId] = useState<string | null>(null);

  const isMutating =
    approveStep.isPending ||
    unapproveStep.isPending ||
    overrideStep.isPending ||
    undoOverride.isPending;
  const pendingStepId = approveStep.isPending ? approveStep.variables.stepId : undefined;

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3" data-testid="approval-timeline-loading">
        <Skeleton className="h-16 w-full rounded-lg" />
        <Skeleton className="h-16 w-full rounded-lg" />
        <Skeleton className="h-16 w-full rounded-lg" />
      </div>
    );
  }

  if (!approvalChain) return null;

  const steps = orderedSteps(approvalChain);
  const isAnyOrder = approvalChain.workflowDefinition.executionMode === 'any_order';

  const handleApprove = (stepId: string) => {
    approveStep.mutate(
      { assetId, stepId },
      {
        onSuccess: () =>
          notificationService.showSuccess(t('approveAssetStep.approvalTimeline.stepApproved')),
        onError: (error) => {
          const { detail } = parseProblemDetail(
            error,
            t('approveAssetStep.approvalTimeline.failedToApproveStep'),
          );
          notificationService.showError(detail);
        },
      },
    );
  };

  const handleConfirmOverride = () => {
    if (overrideTargetStepId === null) return;
    overrideStep.mutate(
      { assetId, stepId: overrideTargetStepId },
      {
        onSuccess: () => {
          setOverrideTargetStepId(null);
          notificationService.showSuccess(t('approveAssetStep.approvalTimeline.stepOverridden'));
        },
        onError: (error) => {
          const { detail } = parseProblemDetail(
            error,
            t('approveAssetStep.approvalTimeline.failedToOverrideStep'),
          );
          notificationService.showError(detail);
        },
      },
    );
  };

  const handleConfirmUndoOverride = () => {
    if (undoOverrideTargetStepId === null) return;
    undoOverride.mutate(
      { assetId, stepId: undoOverrideTargetStepId },
      {
        onSuccess: () => {
          setUndoOverrideTargetStepId(null);
          notificationService.showSuccess(
            t('approveAssetStep.approvalTimeline.stepOverrideUndone'),
          );
        },
        onError: (error) => {
          const { detail } = parseProblemDetail(
            error,
            t('approveAssetStep.approvalTimeline.failedToUndoOverride'),
          );
          notificationService.showError(detail);
        },
      },
    );
  };

  const handleConfirmUnapprove = () => {
    if (unapproveTargetStepId === null) return;
    unapproveStep.mutate(
      { assetId, stepId: unapproveTargetStepId },
      {
        onSuccess: () => {
          setUnapproveTargetStepId(null);
          notificationService.showSuccess(t('approveAssetStep.approvalTimeline.stepUnapproved'));
        },
        onError: (error) => {
          const { detail } = parseProblemDetail(
            error,
            t('approveAssetStep.approvalTimeline.failedToUnapproveStep'),
          );
          notificationService.showError(detail);
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-sm" data-testid="approval-timeline">
      <ChainSummary chain={approvalChain} />

      <ol className="flex flex-col">
        {steps.map((step, index) => (
          <StepRow
            key={step.id}
            step={step}
            isLast={index === steps.length - 1}
            reviewers={stepReviewers(step)}
            currentUserId={currentUserId}
            canOverride={canOverride}
            isMutating={isMutating}
            isPending={pendingStepId === step.id}
            onApprove={() => handleApprove(step.id)}
            onRequestUnapprove={() => setUnapproveTargetStepId(step.id)}
            onRequestOverride={() => setOverrideTargetStepId(step.id)}
            onRequestUndoOverride={() => setUndoOverrideTargetStepId(step.id)}
          />
        ))}
      </ol>

      <Dialog
        open={unapproveTargetStepId !== null}
        onOpenChange={(next) => {
          if (!next && !unapproveStep.isPending) setUnapproveTargetStepId(null);
        }}
      >
        <DialogContent className="gap-0 p-4 max-w-120" data-testid="unapprove-step-warning-dialog">
          <DialogHeader>
            <DialogTitle>{t('approveAssetStep.approvalTimeline.unapproveThisStep')}</DialogTitle>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-4 pb-10 pt-4">
            <p className="text-foreground-alt">
              {isAnyOrder
                ? t('approveAssetStep.approvalTimeline.unapprovingClearsOnlyThisStep')
                : t('approveAssetStep.approvalTimeline.unapprovingWillResetThisStep')}
            </p>
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setUnapproveTargetStepId(null)}
              disabled={unapproveStep.isPending}
            >
              {t('approveAssetStep.approvalTimeline.cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleConfirmUnapprove}
              disabled={unapproveStep.isPending}
              data-testid="unapprove-step-confirm"
            >
              {unapproveStep.isPending && <Spinner />}
              {unapproveStep.isPending
                ? t('approveAssetStep.approvalTimeline.unapproving')
                : t('approveAssetStep.approvalTimeline.yesUnapprove')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={overrideTargetStepId !== null}
        onOpenChange={(next) => {
          if (!next && !overrideStep.isPending) setOverrideTargetStepId(null);
        }}
      >
        <DialogContent className="gap-0 p-4 max-w-120" data-testid="override-step-warning-dialog">
          <DialogHeader>
            <DialogTitle>{t('approveAssetStep.approvalTimeline.overrideThisStep')}</DialogTitle>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-4 pb-10 pt-4">
            <p className="text-foreground-alt">
              {t('approveAssetStep.approvalTimeline.overridingClearsTheStep')}
            </p>
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOverrideTargetStepId(null)}
              disabled={overrideStep.isPending}
            >
              {t('approveAssetStep.approvalTimeline.cancel')}
            </Button>
            <Button
              type="button"
              onClick={handleConfirmOverride}
              disabled={overrideStep.isPending}
              data-testid="override-step-confirm"
            >
              {overrideStep.isPending && <Spinner />}
              {overrideStep.isPending
                ? t('approveAssetStep.approvalTimeline.overriding')
                : t('approveAssetStep.approvalTimeline.yesOverride')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={undoOverrideTargetStepId !== null}
        onOpenChange={(next) => {
          if (!next && !undoOverride.isPending) setUndoOverrideTargetStepId(null);
        }}
      >
        <DialogContent className="gap-0 p-4 max-w-120" data-testid="undo-override-warning-dialog">
          <DialogHeader>
            <DialogTitle>{t('approveAssetStep.approvalTimeline.undoOverrideThisStep')}</DialogTitle>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-4 pb-10 pt-4">
            <p className="text-foreground-alt">
              {isAnyOrder
                ? t('approveAssetStep.approvalTimeline.undoingOverrideRestoresReviewers')
                : t('approveAssetStep.approvalTimeline.undoingOverrideResetsLaterSteps')}
            </p>
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setUndoOverrideTargetStepId(null)}
              disabled={undoOverride.isPending}
            >
              {t('approveAssetStep.approvalTimeline.cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleConfirmUndoOverride}
              disabled={undoOverride.isPending}
              data-testid="undo-override-confirm"
            >
              {undoOverride.isPending && <Spinner />}
              {undoOverride.isPending
                ? t('approveAssetStep.approvalTimeline.undoingOverride')
                : t('approveAssetStep.approvalTimeline.yesUndoOverride')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

ApprovalTimeline.displayName = 'ApprovalTimeline';

function ChainSummary({ chain }: { chain: AssetApprovalChain }) {
  const ModeIcon =
    chain.workflowDefinition.executionMode === 'any_order' ? ShuffleIcon : ListOrderedIcon;

  return (
    <div
      className="flex items-center justify-between gap-2"
      data-testid="approval-timeline-summary"
    >
      <span className="text-xs text-muted-foreground">{stepsApprovedLabel(chain)}</span>
      <span
        className="flex items-center gap-1 text-xxs leading-6 text-foreground-subtle font-semibold"
        data-testid="approval-timeline-execution-mode"
      >
        <ModeIcon className="size-3 shrink-0" aria-hidden />
        {executionModeLabel(chain)}
      </span>
    </div>
  );
}

interface StepRowProps {
  step: AssetApprovalChainStep;
  isLast: boolean;
  reviewers: StepReviewer[];
  currentUserId: string | undefined;
  canOverride: boolean;
  isMutating: boolean;
  isPending: boolean;
  onApprove: (reviewerUserId: string) => void;
  onRequestUnapprove: () => void;
  onRequestOverride: () => void;
  onRequestUndoOverride: () => void;
}

function StepRow({
  step,
  isLast,
  reviewers,
  currentUserId,
  canOverride,
  isMutating,
  isPending,
  onApprove,
  onRequestUnapprove,
  onRequestOverride,
  onRequestUndoOverride,
}: StepRowProps) {
  const { t } = useTranslation('projects');

  const isOverridden = stepOverride(step) !== null;
  const isMet = step.state === 'met';
  const isActionable = step.state === 'actionable';
  const isLocked = step.state === 'locked';

  /*
   * Whether anyone other than the caller still owes an approval — the question the
   * step-level override answers. A step with no reviewers and no role counts: nobody can
   * approve it the ordinary way, so the override is the only way past it. So does a
   * role step whose count the approvals so far, and the caller's own, still fall short
   * of, since the holders who have not approved are never listed. Where the caller is
   * the only one outstanding, their own approval finishes the step by itself.
   */
  const approvedCount = reviewers.filter((reviewer) => reviewer.isApproved).length;
  const callersOwnApproval = step.canCurrentUserApprove ? 1 : 0;
  const othersOutstanding =
    (reviewers.length === 0 && !step.approverRole) ||
    reviewers.some((reviewer) => reviewer.userId !== currentUserId && !reviewer.isApproved) ||
    (!!step.approverRole && approvedCount + callersOwnApproval < requiredApprovalCount(step));

  return (
    <li
      className="relative flex gap-3 pb-5 last:pb-0"
      data-testid="approval-timeline-step"
      data-step-state={step.state}
    >
      {/* left-3 is the node column's midpoint. The line overhangs its own row by
          the 4px a size-6 node sits inside an h-8 row box, or it would stop short
          of the node below. */}
      {!isLast && (
        <span
          className={clsx(
            'absolute left-3 top-7 -bottom-1 w-px -translate-x-1/2',
            isMet ? 'bg-positive-solid' : 'bg-border',
          )}
          aria-hidden
        />
      )}

      {/* h-8 matches the title row, so the node reads as centred on the step name. */}
      <span className="relative z-10 flex h-8 w-6 shrink-0 items-center justify-center" aria-hidden>
        <span
          data-testid="approval-timeline-node"
          data-marker={isMet ? 'check' : isActionable ? 'dot' : 'lock'}
          className={clsx(
            'flex size-6 items-center justify-center rounded-full border',
            isMet && 'border-positive-solid bg-positive-solid text-positive-solid-foreground',
            isActionable && 'border-muted-foreground bg-background',
            isLocked && 'border-border bg-background',
          )}
        >
          {isMet && <CheckIcon className="size-3" strokeWidth={3} />}
          {isActionable && <CircleDotIcon className="size-3" />}
          {isLocked && <LockIcon className="text-muted-foreground size-3" />}
        </span>
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex min-h-8 items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <TruncatedText value={step.name} className="font-medium" />
            {isMet && (
              <Badge variant="positive" size="sm" roundness="full" className="shrink-0">
                {t('approveAssetStep.approvalTimeline.approvedBadge')}
              </Badge>
            )}
            {isActionable && (
              <Badge variant="warning" size="sm" roundness="full" className="shrink-0">
                {t('approveAssetStep.approvalTimeline.inProgressBadge')}
              </Badge>
            )}
          </div>

          {canOverride && othersOutstanding && isActionable && !isOverridden && (
            <Button
              variant="outline"
              size="mini"
              className="shrink-0"
              disabled={isMutating}
              onClick={onRequestOverride}
              aria-label={t('approveAssetStep.approvalTimeline.overrideStep', { step: step.name })}
              data-testid="approval-timeline-override"
            >
              <CheckCheckIcon className="size-3" />
              {t('approveAssetStep.approvalTimeline.approve')}
            </Button>
          )}

          {canOverride && isOverridden && (
            <Button
              variant="secondary"
              size="mini"
              className="shrink-0"
              disabled={isMutating}
              onClick={onRequestUndoOverride}
              aria-label={t('approveAssetStep.approvalTimeline.undoOverrideForStep', {
                step: step.name,
              })}
              data-testid="approval-timeline-undo-override"
            >
              <RotateCcwIcon className="size-3" />
              {t('approveAssetStep.approvalTimeline.unapprove')}
            </Button>
          )}
        </div>

        <span className="text-xs text-muted-foreground">{stepStatusLabel(step)}</span>

        {reviewers.length === 0 && !step.approverRole ? (
          <span className="text-xs text-muted-foreground">
            {t('approveAssetStep.approvalTimeline.noReviewersAssigned')}
          </span>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {reviewers.map((reviewer) => (
              <ReviewerRow
                key={reviewer.userId}
                reviewer={reviewer}
                canApprove={!isOverridden && isActionable && reviewer.userId === currentUserId}
                canUnapprove={
                  !isOverridden && !!reviewer.approval && reviewer.userId === currentUserId
                }
                stepName={step.name}
                isMutating={isMutating}
                isPending={isPending}
                onApprove={() => onApprove(reviewer.userId)}
                onRequestUnapprove={onRequestUnapprove}
              />
            ))}
            {step.approverRole && (
              <RoleApproverRow
                roleName={step.approverRole.name}
                canApprove={
                  !isOverridden &&
                  isActionable &&
                  step.canCurrentUserApprove &&
                  !reviewers.some((reviewer) => reviewer.userId === currentUserId)
                }
                isMutating={isMutating}
                isPending={isPending}
                onApprove={() => currentUserId && onApprove(currentUserId)}
              />
            )}
          </ul>
        )}
      </div>
    </li>
  );
}

interface ReviewerRowProps {
  reviewer: StepReviewer;
  stepName: string;
  canApprove: boolean;
  canUnapprove: boolean;
  isMutating: boolean;
  isPending: boolean;
  onApprove: () => void;
  onRequestUnapprove: () => void;
}

function ReviewerRow({
  reviewer,
  stepName,
  canApprove,
  canUnapprove,
  isMutating,
  isPending,
  onApprove,
  onRequestUnapprove,
}: ReviewerRowProps) {
  const { t } = useTranslation('projects');
  const { isApproved, approvedAt } = reviewer;

  return (
    <li
      className="flex min-h-8 items-center justify-between gap-2"
      data-testid="approval-timeline-reviewer"
      data-approved={isApproved ? 'true' : 'false'}
    >
      <div className="flex min-w-0 items-center gap-2">
        <Avatar size="mini" roundness="full" className={clsx(!isApproved && 'opacity-50')}>
          {reviewer.avatarUrl && <AvatarImage src={reviewer.avatarUrl} alt={reviewer.name} />}
          <AvatarFallback size="mini" roundness="full">
            {getInitials(reviewer.name)}
          </AvatarFallback>
        </Avatar>
        <TruncatedText
          value={reviewer.name}
          className={clsx('text-sm', !isApproved && 'text-muted-foreground')}
        />
      </div>

      {isApproved ? (
        <div className="flex shrink-0 items-center gap-2">
          <span className="flex flex-col items-end">
            <span className="text-positive-solid flex items-center gap-1 text-xs">
              <CheckIcon className="size-3.5" aria-hidden />
              {approvedAt && formatShortDate(approvedAt)}
            </span>
            {reviewer.approvedByName && (
              <span
                className="text-muted-foreground text-xs"
                data-testid="approval-timeline-approved-by"
              >
                {t('approveAssetStep.approvalTimeline.approvedByName', {
                  name: reviewer.approvedByName,
                })}
              </span>
            )}
          </span>
          {canUnapprove && (
            <Button
              variant="ghost"
              size="mini"
              className="shrink-0"
              disabled={isMutating}
              onClick={onRequestUnapprove}
              aria-label={t('approveAssetStep.approvalTimeline.unapproveStep', { step: stepName })}
              data-testid="approval-timeline-unapprove"
            >
              <RotateCcwIcon className="size-3" />
              {t('approveAssetStep.approvalTimeline.unapprove')}
            </Button>
          )}
        </div>
      ) : (
        canApprove && (
          <Button
            variant="outline"
            size="mini"
            className="shrink-0"
            disabled={isMutating}
            onClick={onApprove}
            data-testid="approval-timeline-approve"
          >
            {isPending ? <Spinner className="size-3" /> : <CheckIcon className="size-3" />}
            {t('approveAssetStep.approvalTimeline.approve')}
          </Button>
        )
      )}
    </li>
  );
}
